import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  REGULATION_PACK_LABELS,
  type RegulationPack,
  type RegulationPackAssessment,
} from '../types';
import { REGULATION_PACK_ASSESSMENT_QUESTIONS } from './regulationPackAssessmentConfig';

const CRA_CLASS_KEYS = [
  'importantClassI',
  'importantClassII',
  'criticalProduct',
];

const PPE_CATEGORY_KEYS = ['minimalRisk', 'categoryII', 'categoryIIIRisk'];

const CONSTRUCTION_AVS_KEYS = [
  'avsSystem1Plus',
  'avsSystem1',
  'avsSystem2Plus',
  'avsSystem3Plus',
  'avsSystem3',
  'avsSystem4',
];

interface RegulationPackAssessmentsProps {
  assessments: RegulationPackAssessment[];
  savingPack: RegulationPack | null;
  disabled?: boolean;
  onAssess: (
    pack: RegulationPack,
    inputs: Record<string, boolean>
  ) => Promise<void>;
}

export default function RegulationPackAssessments({
  assessments,
  savingPack,
  disabled = false,
  onAssess,
}: RegulationPackAssessmentsProps) {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState<
    Partial<Record<RegulationPack, Record<string, boolean>>>
  >({});

  useEffect(() => {
    const next: Partial<Record<RegulationPack, Record<string, boolean>>> = {};
    assessments.forEach(assessment => {
      const questions =
        REGULATION_PACK_ASSESSMENT_QUESTIONS[assessment.regulationPack];
      if (!questions) return;
      next[assessment.regulationPack] = Object.fromEntries(
        questions.map(key => [key, assessment.inputs[key] ?? false])
      );
    });
    setDrafts(next);
  }, [assessments]);

  const assessable = assessments.filter(
    assessment =>
      REGULATION_PACK_ASSESSMENT_QUESTIONS[assessment.regulationPack]
  );
  if (assessable.length === 0) return null;

  const setInput = (pack: RegulationPack, key: string, value: boolean) => {
    setDrafts(current => {
      const nextInputs = { ...(current[pack] ?? {}) };
      if (pack === 'cra_2024_2847' && value && CRA_CLASS_KEYS.includes(key)) {
        CRA_CLASS_KEYS.forEach(classKey => {
          nextInputs[classKey] = false;
        });
      }
      if (
        pack === 'personal_protective_equipment_2016_425' &&
        value &&
        PPE_CATEGORY_KEYS.includes(key)
      ) {
        PPE_CATEGORY_KEYS.forEach(categoryKey => {
          nextInputs[categoryKey] = false;
        });
        if (key !== 'categoryIIIRisk') {
          nextInputs.singleUnitForIndividual = false;
        }
      }
      if (
        pack === 'construction_products_2024_3110' &&
        value &&
        CONSTRUCTION_AVS_KEYS.includes(key)
      ) {
        CONSTRUCTION_AVS_KEYS.forEach(systemKey => {
          nextInputs[systemKey] = false;
        });
        nextInputs.article14Exemption = false;
      }
      if (
        pack === 'construction_products_2024_3110' &&
        key === 'article14Exemption' &&
        value
      ) {
        CONSTRUCTION_AVS_KEYS.forEach(systemKey => {
          nextInputs[systemKey] = false;
        });
      }
      nextInputs[key] = value;
      return { ...current, [pack]: nextInputs };
    });
  };

  return (
    <section style={{ display: 'grid', gap: 16, marginBottom: 24 }}>
      {assessable.map(assessment => {
        const pack = assessment.regulationPack;
        const questions = REGULATION_PACK_ASSESSMENT_QUESTIONS[pack] ?? [];
        const inputs = drafts[pack] ?? {};
        return (
          <div
            key={pack}
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)',
              padding: 20,
            }}
          >
            <div style={{ marginBottom: 14 }}>
              <div className="page-eyebrow">
                {t('results.packAssessment.eyebrow')}
              </div>
              <h2 style={{ fontSize: 20, margin: '6px 0' }}>
                {REGULATION_PACK_LABELS[pack]}
              </h2>
              {assessment.result !== 'needs_information' && (
                <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
                  <strong>
                    {t('results.packAssessment.result', {
                      result: assessment.result.replace(/_/g, ' '),
                    })}
                  </strong>
                  <div>{assessment.rationale}</div>
                  <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>
                    {assessment.sourceReference}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gap: 10 }}>
              {questions.map(key => (
                <label
                  key={key}
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'flex-start',
                    color: 'var(--text-secondary)',
                    fontSize: 14,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={inputs[key] ?? false}
                    disabled={disabled}
                    onChange={event =>
                      setInput(pack, key, event.target.checked)
                    }
                  />
                  {t(`results.packAssessment.questions.${key}`)}
                </label>
              ))}
            </div>

            <button
              className="button button-primary"
              style={{ marginTop: 16 }}
              disabled={savingPack !== null || disabled}
              onClick={() => onAssess(pack, inputs)}
            >
              {savingPack === pack
                ? t('results.packAssessment.saving')
                : t('results.packAssessment.assess')}
            </button>
          </div>
        );
      })}
    </section>
  );
}
